import {format, parse} from 'date-fns';
import {formatDisplayTimeOnly} from '@/utils/timeDisplayFormat';

/** Pad to 2 digits for month/day. */
const pad2 = (n: number): string => String(Math.trunc(n)).padStart(2, '0');

/** Expand a 2-digit year to 4 digits (pivot at 50: <50 → 2000s, else 1900s). */
const expand2DigitYear = (yy: number): number => (yy < 50 ? 2000 + yy : 1900 + yy);

/** Format a Date as MM-DD-YYYY using local getters. */
const dateToMdY = (d: Date): string =>
  `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}-${d.getFullYear()}`;

/** Stringify a DOB/timestamp-like value: Date instances → ISO, primitives → String(), else ''. */
function rawValueToString(value: unknown): string {
  if (value instanceof Date) return value.toISOString();
  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : '';
}

/** Parse a DOB string into MM-DD-YYYY via the known string patterns; null if none match. */
function parseDobStringPatterns(raw: string): string | null {
  // Already MM-DD-YYYY (allow 1–2 digit month/day)
  const mdY = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (mdY) {
    return `${pad2(Number.parseInt(mdY[1], 10))}-${pad2(Number.parseInt(mdY[2], 10))}-${mdY[3]}`;
  }

  // YYYY-MM-DD (optional time / T suffix)
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  if (ymd) {
    return `${ymd[2]}-${ymd[3]}-${ymd[1]}`;
  }

  // MM-DD-YY (hyphen, two-digit year — e.g. pasted from spreadsheets)
  const mdY2 = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{2})$/);
  if (mdY2) {
    const yyyy = expand2DigitYear(Number.parseInt(mdY2[3], 10));
    return `${pad2(Number.parseInt(mdY2[1], 10))}-${pad2(Number.parseInt(mdY2[2], 10))}-${yyyy}`;
  }

  // MM/DD/YYYY or MM/DD/YY (US-style from APIs / Excel); allow spaces around slashes
  const slashNormalized = raw.replace(/\s*\/\s*/g, '/');
  const slash = slashNormalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (slash) {
    const yyyy =
      slash[3].length === 2
        ? expand2DigitYear(Number.parseInt(slash[3], 10))
        : Number.parseInt(slash[3], 10);
    return `${pad2(Number.parseInt(slash[1], 10))}-${pad2(Number.parseInt(slash[2], 10))}-${yyyy}`;
  }

  return null;
}

/**
 * Normalize DOB to MM-DD-YYYY for UI and CSV export (avoids Excel slash / 2-digit year drift).
 */
export function formatDobForDisplay(value: unknown): string {
  if (value === null || value === undefined || rawValueToString(value).trim() === '') {
    return 'N/A';
  }
  const raw = rawValueToString(value).trim();

  const fromPatterns = parseDobStringPatterns(raw);
  if (fromPatterns !== null) return fromPatterns;

  // ISO datetime
  if (raw.includes('T')) {
    const dt = new Date(raw);
    if (!Number.isNaN(dt.getTime())) {
      return dateToMdY(dt);
    }
  }

  // Fallback: Date.parse (handles some locale strings)
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return dateToMdY(parsed);
  }

  return raw;
}

/** Scheduler card line: e.g. `13 Apr 2000` (from normalized MM-DD-YYYY or raw date). */
export function formatDobAsDayMonYear(value: unknown): string {
  if (value === null || value === undefined || rawValueToString(value).trim() === '') {
    return 'N/A';
  }
  const normalized = formatDobForDisplay(value);
  if (normalized === 'N/A') return 'N/A';
  const mdY = normalized.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (mdY) {
    const parsed = parse(`${mdY[1]}-${mdY[2]}-${mdY[3]}`, 'MM-dd-yyyy', new Date());
    if (!Number.isNaN(parsed.getTime())) {
      return format(parsed, 'd MMM yyyy');
    }
  }
  const raw = rawValueToString(value).trim();
  const fromRaw = new Date(raw);
  if (!Number.isNaN(fromRaw.getTime())) {
    return format(fromRaw, 'd MMM yyyy');
  }
  return normalized;
}

/**
 * CSV cell value so Excel opens DOB as **text** in MM-DD-YYYY (not a locale date with slashes).
 * Plain `04-03-2012` is often auto-parsed as a serial date; `="04-03-2012"` stays consistent.
 */
export function formatDobForExcelCsv(value: unknown): string {
  const s = formatDobForDisplay(value);
  if (s === 'N/A') return 'N/A';
  return `="${s}"`;
}

/** Format a timestamp for the "Updated At" column: MM-DD-YYYY HH:MM AM/PM in browser timezone. */
export function formatTimestampForDisplay(value: unknown): string {
  if (value === null || value === undefined || rawValueToString(value).trim() === '') return '-';
  const d = new Date(rawValueToString(value));
  if (isNaN(d.getTime())) return '-';
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = d.getFullYear();
  const time = formatDisplayTimeOnly(d);
  return `${mm}-${dd}-${yyyy} ${time}`;
}

/**
 * Endorsed Date column: MM-DD-YYYY HH:MM AM/PM in the browser's local timezone
 * (e.g. "05-26-2026 11:17 PM"). Always 12-hour with AM/PM per the column spec,
 * independent of the app-wide 12/24-hour preference. Returns '' when missing/invalid.
 */
export function formatEndorsedDateForDisplay(value: unknown): string {
  if (value === null || value === undefined || rawValueToString(value).trim() === '') return '';
  const d = new Date(rawValueToString(value));
  if (isNaN(d.getTime())) return '';
  // date-fns formats in the runtime's local timezone.
  return format(d, 'MM-dd-yyyy hh:mm a');
}

/**
 * Relationship column labels: shorten OneForma-style phrases for the UI.
 * e.g. "Sibling Relations" → "Sibling", "Twin Relations" / "twin relations" → "twin".
 */
export function normalizeRelationshipLabelForUi(
  value: string | null | undefined,
): string {
  if (value == null) return '';
  let raw = String(value).trim();
  if (!raw) return '';

  if (raw.includes('·')) {
    const parts = raw.split('·').map((p) => p.trim());
    parts[0] = normalizeRelationshipLabelForUi(parts[0]);
    return parts.join(' · ');
  }

  const spaced = raw.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  const lower = spaced.toLowerCase();
  if (lower === 'primary') return 'Primary';
  if (lower === 'secondary') return 'Secondary';
  if (lower === 'sibling relations' || lower === 'siblings relations') {
    return 'Sibling';
  }
  if (
    lower === 'twin relations' ||
    lower === 'twins relations' ||
    lower === 'twins relation' ||
    lower === 'twin relation'
  ) {
    return 'twin';
  }
  if (lower === 'twin' || lower === 'twins') {
    return 'twin';
  }
  return raw;
}

/** Shown in Related name / Relationship / Family code when a primary has no secondaries in the project. */
export const FAMILY_GRID_NOT_APPLICABLE = 'Not Applicable';

/**
 * Primary participant with no linked secondaries (`is_family_group` from API is not true).
 * Family columns should show {@link FAMILY_GRID_NOT_APPLICABLE} instead of empty.
 */
export function isSoloPrimaryNoFamilyMembers(participant: {
  oneforma_primary_user_id?: string | null;
  is_family_group?: boolean;
}): boolean {
  const isPrimary =
    participant.oneforma_primary_user_id === null ||
    participant.oneforma_primary_user_id === undefined ||
    String(participant.oneforma_primary_user_id).trim() === '';
  return isPrimary && participant.is_family_group !== true;
}

/**
 * Participant type label for UI.
 * Source of truth is participants.participant_type; OneForma linkage is fallback for older rows.
 */
export function getParticipantTypeForDisplay(participant: {
  participant_type?: string | null;
  oneforma_primary_user_id?: string | null;
}): 'Primary' | 'Secondary' {
  const participantTypeRaw = String(participant.participant_type ?? '')
    .trim()
    .toLowerCase();
  if (participantTypeRaw === 'primary') return 'Primary';
  if (participantTypeRaw === 'secondary') return 'Secondary';

  const hasOneformaPrimary =
    participant.oneforma_primary_user_id !== null &&
    participant.oneforma_primary_user_id !== undefined &&
    String(participant.oneforma_primary_user_id).trim() !== '';
  return hasOneformaPrimary ? 'Secondary' : 'Primary';
}
